import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  DEFAULT_UI_THEME_PREFERENCE,
  FRAPPE_UI_THEME_OPTIONS,
  isUiThemePreference,
  preferenceFromFrappeValue,
  UI_THEME_DEFINITIONS,
  UI_THEME_STORAGE_KEY,
  type UiTheme,
  type UiThemePreference,
} from '@/constants/uiThemes'
import { useFrappeCallAPIStore } from '@/stores/apis/frappeCallAPI'

const SET_UI_THEME_METHOD = 'erp_2work.erp_2work.controller.ui_theme.set_ui_theme'

const readCachedPreference = (): UiThemePreference => {
  try {
    const cached = localStorage.getItem(UI_THEME_STORAGE_KEY)
    return isUiThemePreference(cached) ? cached : DEFAULT_UI_THEME_PREFERENCE
  } catch {
    return DEFAULT_UI_THEME_PREFERENCE
  }
}

const writeCachedPreference = (preference: UiThemePreference): void => {
  try {
    localStorage.setItem(UI_THEME_STORAGE_KEY, preference)
  } catch {
    // Storage unavailable (private mode), the preference still lives on the User.
  }
}

const prefersDarkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

/** Keep in sync with the anti-flash script in index.html. */
const applyUiTheme = (theme: UiTheme): void => {
  const definition = UI_THEME_DEFINITIONS[theme]
  const root = document.documentElement

  root.setAttribute('data-theme', theme)
  root.setAttribute('data-contrast', definition.isHighContrast ? 'high' : 'normal')
  root.classList.toggle('dark', definition.colorScheme === 'dark')
  // Native controls / scrollbars of the web app. <iframe> elements stay `light`
  // regardless (custom-base.css), matching the always-light embedded Frappe pages.
  root.style.colorScheme = definition.colorScheme
}

/**
 * Message understood by the Frappe iframe side (erp_2work tw_theme.js).
 * `mode` is the light / dark scheme Frappe's native theme switches on, `theme`
 * the full UI theme for the high contrast overrides.
 */
const buildIframeThemeMessage = (
  theme: UiTheme,
): { mode: 'dark' | 'light'; theme: UiTheme; type: string } => ({
  mode: UI_THEME_DEFINITIONS[theme].colorScheme,
  theme,
  type: 'tw-theme',
})

const broadcastThemeToIframes = (theme: UiTheme): HTMLIFrameElement[] => {
  const message = buildIframeThemeMessage(theme)
  const iframes = Array.from(document.querySelectorAll('iframe'))
  iframes.forEach((iframe) => {
    iframe.contentWindow?.postMessage(message, '*')
  })
  return iframes
}

/** Upper bound for waiting on the iframes during a theme switch. */
const IFRAME_THEME_ACK_TIMEOUT_MS = 250

/**
 * Resolve once every Frappe iframe confirmed (`tw-theme-applied`) that it switched,
 * or after the timeout, iframes without tw_theme.js (PDFs, external pages) never answer.
 */
const waitForIframeThemeAck = async (iframes: HTMLIFrameElement[]): Promise<void> => {
  const pending = new Set(
    iframes.map((iframe) => iframe.contentWindow).filter((win) => win !== null),
  )
  if (pending.size === 0) return

  await new Promise<void>((resolve) => {
    const listenerController = new AbortController()
    const finish = (): void => {
      listenerController.abort()
      resolve()
    }

    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        const payload: unknown = event.data
        if (!payload || typeof payload !== 'object' || !('type' in payload)) return
        if (payload.type !== 'tw-theme-applied') return

        // Match by identity, `instanceof Window` fails for another realm's window.
        pending.forEach((win) => {
          if (win === event.source) pending.delete(win)
        })
        if (pending.size === 0) finish()
      },
      { signal: listenerController.signal },
    )
    setTimeout(finish, IFRAME_THEME_ACK_TIMEOUT_MS)
  })
}

/** Class on <html> that suspends CSS transitions while the theme switches (custom-base.css). */
const THEME_SWITCHING_CLASS = 'tw-theme-switching'

const prefersReducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

/**
 * Switch the theme with ONE uniform crossfade (View Transitions API): the browser snapshots
 * the page, incl. the iframes, before and after, and fades between the two. Per-element CSS
 * transitions would fade at different speeds (inherited colors, stacked translucent layers,
 * iframes switching later), so they are suspended meanwhile. Without API support or with
 * reduced motion the switch is instant.
 */
const switchUiTheme = (theme: UiTheme): void => {
  const root = document.documentElement
  const applyAll = async (): Promise<void> => {
    applyUiTheme(theme)
    await waitForIframeThemeAck(broadcastThemeToIframes(theme))
  }

  if (
    typeof document.startViewTransition !== 'function' ||
    prefersReducedMotionMediaQuery.matches
  ) {
    applyAll().catch(() => undefined)
    return
  }

  root.classList.add(THEME_SWITCHING_CLASS)
  const transition = document.startViewTransition(applyAll)
  transition.finished
    .catch(() => undefined)
    .finally(() => {
      root.classList.remove(THEME_SWITCHING_CLASS)
    })
}

/** The window of the embedded iframe that sent a message, if it is one of ours. */
const findOwnIframeWindow = (source: MessageEventSource | null): undefined | Window => {
  const iframe = Array.from(document.querySelectorAll('iframe')).find(
    (element) => element.contentWindow !== null && element.contentWindow === source,
  )
  return iframe?.contentWindow ?? undefined
}

/**
 * Per-user UI theme. The preference is stored on the User (`tw_ui_theme`, editable
 * in the Frappe user form under "Desk Settings" or via the topbar menu) and cached
 * in localStorage so the next page load renders the right theme immediately.
 */
const useThemeStore = defineStore('ThemeStore', () => {
  const callAPIStore = useFrappeCallAPIStore()

  const preference = ref<UiThemePreference>(readCachedPreference())
  const prefersDark = ref(prefersDarkMediaQuery.matches)

  prefersDarkMediaQuery.addEventListener('change', (event) => {
    prefersDark.value = event.matches
  })

  const resolvedTheme = computed<UiTheme>(() => {
    if (preference.value === 'system') {
      return prefersDark.value ? 'dark' : 'light'
    }
    return preference.value
  })

  watch(
    resolvedTheme,
    (theme, previousTheme) => {
      // No crossfade on the initial apply, only when the theme actually switches.
      if (previousTheme === undefined) {
        applyUiTheme(theme)
        broadcastThemeToIframes(theme)
        return
      }
      switchUiTheme(theme)
    },
    { immediate: true },
  )

  watch(preference, writeCachedPreference)

  /** Apply the value read from the User document (Frappe option label). */
  const applyUserPreference = (frappeValue: unknown): void => {
    preference.value = preferenceFromFrappeValue(frappeValue) ?? DEFAULT_UI_THEME_PREFERENCE
  }

  /**
   * Back to the default (System) without saving it to any user — on logout, so the login page and
   * the next user on this browser do not briefly see the previous user's theme.
   */
  const resetPreference = (): void => {
    preference.value = DEFAULT_UI_THEME_PREFERENCE
  }

  /**
   * Change the theme and persist it on the current user. The backend also switches Frappe's
   * native desk theme, so desk iframes loaded later already boot in the right scheme; the
   * iframes open right now switch live via the broadcast in the `resolvedTheme` watcher, no
   * reload, which would blank them for the whole desk boot.
   */
  const savePreference = async (value: UiThemePreference): Promise<void> => {
    const previous = preference.value
    preference.value = value

    try {
      await callAPIStore.post(SET_UI_THEME_METHOD, { theme: FRAPPE_UI_THEME_OPTIONS[value] })
    } catch (saveError) {
      // Roll back unless the user picked yet another theme in the meantime.
      if (preference.value === value) {
        preference.value = previous
      }
      console.error('[Theme] Failed to save the UI theme:', saveError)
    }
  }

  window.addEventListener('message', (event: MessageEvent) => {
    const sourceWindow = findOwnIframeWindow(event.source)
    const payload: unknown = event.data
    if (!sourceWindow || !payload || typeof payload !== 'object' || !('type' in payload)) return

    // A Frappe iframe finished loading and asks for the current theme.
    if (payload.type === 'tw-theme-request') {
      sourceWindow.postMessage(buildIframeThemeMessage(resolvedTheme.value), '*')
    }

    // The own User was saved in the Frappe user form with a new theme.
    // Already persisted by the form save (TWorkUser syncs desk_theme and clears the cache).
    if (payload.type === 'tw-ui-theme-changed' && 'value' in payload) {
      applyUserPreference(payload.value)
    }
  })

  return {
    applyUserPreference,
    preference,
    resetPreference,
    resolvedTheme,
    savePreference,
  }
})

export { useThemeStore }
